import {
	NVM_LOCAL_ORIGIN,
	NVM_PRODUCTION_ORIGIN,
	isLocalHostname,
} from "./origins";

declare global {
	interface NVMGlobalRegistry {}

	interface Window {
		nvm: NVMGlobalRegistry;
	}
}

type WorkerEndpoint = {
	apiBaseUrl: string;
	socketHost: string;
};

function getWorkerEndpointFromOrigin(origin: string): WorkerEndpoint {
	const url = new URL(origin);
	return {
		apiBaseUrl: url.origin,
		socketHost: url.host,
	};
}

function getCurrentScriptEndpoint(): WorkerEndpoint | null {
	const currentScript = document.currentScript;
	if (!(currentScript instanceof HTMLScriptElement) || !currentScript.src) {
		return null;
	}

	try {
		const url = new URL(currentScript.src, window.location.href);
		if (url.protocol !== "http:" && url.protocol !== "https:") {
			return null;
		}

		return {
			apiBaseUrl: url.origin,
			socketHost: url.host,
		};
	} catch {
		return null;
	}
}

function getFallbackWorkerEndpoint(): WorkerEndpoint {
	if (isLocalHostname(window.location.hostname)) {
		return getWorkerEndpointFromOrigin(NVM_LOCAL_ORIGIN);
	}

	return getWorkerEndpointFromOrigin(NVM_PRODUCTION_ORIGIN);
}

const WORKER_ENDPOINT = getCurrentScriptEndpoint() ?? getFallbackWorkerEndpoint();

export function getApiBaseUrl(): string {
	return WORKER_ENDPOINT.apiBaseUrl;
}

export function getSocketHost(): string {
	return WORKER_ENDPOINT.socketHost;
}

export function getNVMGlobal(): NVMGlobalRegistry {
	window.nvm ||= {};
	return window.nvm;
}

export function registerNVMFeature<TKey extends keyof NVMGlobalRegistry>(
	key: TKey,
	value: NonNullable<NVMGlobalRegistry[TKey]>,
): NonNullable<NVMGlobalRegistry[TKey]> {
	const nvm = getNVMGlobal();
	(nvm as Record<string, unknown>)[key as string] = value;
	return value;
}

export function onDocumentReady(callback: () => void): void {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", callback);
		return;
	}

	callback();
}

export class NVMApiClient<TData extends Record<string, string | null>> {
	public data: TData | null = null;

	constructor(
		private readonly endpoint: string,
		private readonly attributeName: string,
		private readonly eventName: string,
		private readonly normalize: (value: unknown) => TData | null,
	) {}

	async refresh(): Promise<TData | null> {
		const data = await this.fetchData();
		if (data) return data;

		// Single retry after brief delay for flaky connections
		await new Promise((resolve) => setTimeout(resolve, 1500));
		return this.fetchData();
	}

	private async fetchData(): Promise<TData | null> {
		try {
			const response = await fetch(`${getApiBaseUrl()}${this.endpoint}`);
			if (!response.ok) return null;

			const data = this.normalize(await response.json());
			if (!data) return null;

			return this.commitData(data);
		} catch {
			return null;
		}
	}

	protected commitData(data: TData): TData {
		this.data = data;
		this.updateElements();
		window.dispatchEvent(new CustomEvent(this.eventName, { detail: data }));

		return data;
	}

	private updateElements(): void {
		const data = this.data;
		if (!data) {
			return;
		}

		try {
			document.querySelectorAll(`[${this.attributeName}]`).forEach((element) => {
				const key = element.getAttribute(this.attributeName);
				if (!key || !(key in data)) {
					return;
				}

				element.textContent = data[key as keyof TData] ?? "";
			});
		} catch {
			// Silent failure - never break host page
		}
	}
}
